import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderDetails.css';
import './OpenOrdersAnalysis.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function OpenOrdersAnalysis({ warehouse, country, orderType, excludeCarriers, onBack }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'gen_date', direction: 'asc' });

  // Local filters
  const [localOrderType, setLocalOrderType] = useState(orderType || 'All');
  const [localExcludeCarriers, setLocalExcludeCarriers] = useState(excludeCarriers || false);
  const [availableOrderTypes, setAvailableOrderTypes] = useState([]);
  // Empty = all pickers
  const [selectedPickers, setSelectedPickers] = useState([]);

  // Map order type codes to readable labels
  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  useEffect(() => {
    fetchOrders(true);

    const refreshInterval = setInterval(() => {
      fetchOrders(false);
    }, 60000); // 60 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [warehouse, country, localOrderType, localExcludeCarriers]);

  const fetchOrders = async (showLoading) => {
    try {
      if (showLoading) setLoading(true);
      const params = new URLSearchParams();
      if (country && country !== 'All') params.append('country', country);
      if (localOrderType && localOrderType !== 'All') params.append('orderType', localOrderType);
      if (localExcludeCarriers) params.append('excludeCarriers', 'true');

      const response = await axios.get(`${API_URL}/open-orders/details/${encodeURIComponent(warehouse)}?${params}`);
      const rows = (response.data.orders || []).map(o => ({
        ...o,
        picker_name: (o.picker_name || 'Unassigned').trim()
      }));
      setOrders(rows);
      setError(null);

      // Only grow the order type list so picking a type doesn't hide the others
      setAvailableOrderTypes(prev => [...new Set([
        ...prev,
        ...rows.map(o => o.order_type).filter(Boolean)
      ])].sort());
    } catch (err) {
      console.error('Error fetching open orders:', err);
      setError('Failed to load open orders');
    } finally {
      setLoading(false);
    }
  };

  // Picker list with counts, oldest ticket first
  const pickerStats = Object.values(orders.reduce((acc, o) => {
    const p = acc[o.picker_name] || { name: o.picker_name, count: 0, oldest: 0 };
    p.count += 1;
    p.oldest = Math.max(p.oldest, Number(o.days_to_pick) || 0);
    acc[o.picker_name] = p;
    return acc;
  }, {})).sort((a, b) => b.oldest - a.oldest || a.name.localeCompare(b.name));

  const togglePicker = (name) => {
    setSelectedPickers(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]
    );
  };

  const filteredOrders = selectedPickers.length === 0
    ? orders
    : orders.filter(o => selectedPickers.includes(o.picker_name));

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return sortConfig.direction === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⬍';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone issues
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US');
  };

  const totals = {
    picks: filteredOrders.length,
    lines: filteredOrders.reduce((sum, o) => sum + (Number(o.line_count) || 0), 0),
    quantity: filteredOrders.reduce((sum, o) => sum + (Number(o.qty_count) || 0), 0),
    weight: filteredOrders.reduce((sum, o) => sum + (Number(o.weight_count) || 0), 0),
    dollars: filteredOrders.reduce((sum, o) => sum + (Number(o.value_count) || 0), 0),
    avgDaysOpen: filteredOrders.length > 0
      ? filteredOrders.reduce((sum, o) => sum + (Number(o.days_to_pick) || 0), 0) / filteredOrders.length
      : 0
  };

  if (loading) {
    return <div className="order-details-loading">Loading open orders...</div>;
  }

  if (error) {
    return <div className="order-details-error">{error}</div>;
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Picker List
        </button>
        <h2>{warehouse} - All Open Orders</h2>
        <p className="total-picks">Total Open Picks: {totals.picks}</p>
        <p className="total-picks">Avg Days Open: {totals.avgDaysOpen.toFixed(2)}</p>
      </div>

      <div className="filters-section" style={{ marginBottom: '20px' }}>
        <div className="filter-group">
          <label>Order Type:</label>
          <select value={localOrderType} onChange={(e) => setLocalOrderType(e.target.value)}>
            <option value="All">All</option>
            {availableOrderTypes.map(type => (
              <option key={type} value={type}>
                {orderTypeLabels[type] || type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={localExcludeCarriers}
              onChange={(e) => setLocalExcludeCarriers(e.target.checked)}
            />
            Exclude Carriers: Z/WC/HFSK
          </label>
        </div>

        <div className="picker-chip-group">
          <label>Pickers:</label>
          <div className="picker-chips">
            <button
              className={`picker-chip${selectedPickers.length === 0 ? ' active' : ''}`}
              onClick={() => setSelectedPickers([])}
            >
              All Pickers
            </button>
            {pickerStats.map(p => (
              <button
                key={p.name}
                className={`picker-chip${selectedPickers.includes(p.name) ? ' active' : ''}`}
                onClick={() => togglePicker(p.name)}
                title={`Oldest ticket: ${p.oldest} days open`}
              >
                {p.name} <span className="picker-chip-count">{p.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="order-details-table-container">
        <table className="order-details-table analysis-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('picker_name')}>
                Picker{getSortIndicator('picker_name')}
              </th>
              <th onClick={() => handleSort('pick_no')}>
                Pick No{getSortIndicator('pick_no')}
              </th>
              <th onClick={() => handleSort('cust_no')}>
                Cust No{getSortIndicator('cust_no')}
              </th>
              <th onClick={() => handleSort('days_to_pick')}>
                Days Open{getSortIndicator('days_to_pick')}
              </th>
              <th onClick={() => handleSort('line_count')}>
                Lines{getSortIndicator('line_count')}
              </th>
              <th onClick={() => handleSort('qty_count')}>
                Quantity{getSortIndicator('qty_count')}
              </th>
              <th onClick={() => handleSort('weight_count')}>
                Weight{getSortIndicator('weight_count')}
              </th>
              <th onClick={() => handleSort('value_count')}>
                Dollars{getSortIndicator('value_count')}
              </th>
              <th onClick={() => handleSort('carrier')}>
                Carrier{getSortIndicator('carrier')}
              </th>
              <th onClick={() => handleSort('order_type')}>
                Type{getSortIndicator('order_type')}
              </th>
              <th onClick={() => handleSort('gen_date')}>
                Gen Date{getSortIndicator('gen_date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((order) => (
              <tr key={`${order.pick_no}-${order.picker_name}`}>
                <td>{order.picker_name}</td>
                <td>{order.pick_no}</td>
                <td>{order.cust_no || ''}</td>
                <td>{order.days_to_pick || 0}</td>
                <td>{order.line_count?.toLocaleString() || 0}</td>
                <td>{order.qty_count?.toLocaleString() || 0}</td>
                <td>{Math.round((order.weight_count || 0) / 10000).toLocaleString()} lbs</td>
                <td>${Math.round((Number(order.value_count) || 0) / 100).toLocaleString()}</td>
                <td>{order.carrier || ''}</td>
                <td>{order.order_type || ''}</td>
                <td>{formatDate(order.gen_date)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td><strong>Totals:</strong></td>
              <td><strong>{totals.picks.toLocaleString()} picks</strong></td>
              <td></td>
              <td><strong>Avg: {totals.avgDaysOpen.toFixed(2)}</strong></td>
              <td><strong>{totals.lines.toLocaleString()}</strong></td>
              <td><strong>{totals.quantity.toLocaleString()}</strong></td>
              <td><strong>{Math.round(totals.weight / 10000).toLocaleString()} lbs</strong></td>
              <td><strong>${Math.round(totals.dollars / 100).toLocaleString()}</strong></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default OpenOrdersAnalysis;
